'use client';

import { useEffect } from 'react';

/**
 * Google Fonts loader for the bio page.
 *
 * Injects one `<link rel="stylesheet" media="all">` per href into `<head>` via
 * direct DOM manipulation. The faces apply immediately; Google's `font-display:
 * swap` keeps text visible during the fetch, so this doesn't block first paint.
 * With JS disabled we emit a `<noscript>` companion link as a fallback.
 *
 * (The links are NOT injected `media="print"` + swapped to `"all"` on `load`:
 * that deferral broke on disk-cache hits / StrictMode remounts, leaving the
 * stylesheet on `media="print"` so its `@font-face` rules never applied and
 * text fell back to a system font — see the effect body.)
 *
 * Why DOM injection (not a React-rendered `<link>`): direct injection lets us
 * dedupe by `data-bio-font` and keep the link in `<head>` across route changes
 * without React's `precedence` hoisting rewriting it.
 */
interface Props {
  hrefs: string[];
}

export function DeferredFontLinks({ hrefs }: Props) {
  useEffect(() => {
    if (hrefs.length === 0) return;
    // Inject each Google Fonts stylesheet as a plain `media="all"` link so its
    // `@font-face` rules apply immediately.
    //
    // (Previously these were injected `media="print"` and swapped to `"all"` on
    // the `load` event to avoid render-blocking. But a stylesheet served from
    // DISK CACHE can finish before the `load` listener is attached — or under
    // StrictMode's double-mount the reused link never got flipped — leaving
    // `media="print"` forever: the font was fetched (200 OK) but its rules
    // never applied on screen, so the text fell back to a system font and
    // rendered in the wrong weight (QA: "Lexend Semi Bold renders thin even
    // though the CSS is correct and the stylesheet is 200 OK"). Google Fonts
    // already emit `font-display: swap`, so a plain link doesn't block the first
    // paint of text — it just guarantees the face actually applies.)
    const links: HTMLLinkElement[] = [];
    for (const href of hrefs) {
      const existing = document.head.querySelector<HTMLLinkElement>(
        `link[rel="stylesheet"][data-bio-font="${cssAttrEscape(href)}"]`,
      );
      if (existing) {
        // A prior instance may have left it on `media="print"` — ensure it's
        // applied.
        existing.media = 'all';
        links.push(existing);
        continue;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.media = 'all';
      link.setAttribute('data-bio-font', href);
      document.head.appendChild(link);
      links.push(link);
    }
    return () => {
      // No cleanup — leaving the stylesheet in place across
      // route changes is desirable (browser cache + font
      // metrics stay warm). We only `removeChild` if the
      // component is being unmounted for good. Most bio
      // navigations stay within the same family of fonts; if
      // a different bio mounts with a different font, the
      // effect's add-only path injects the new one alongside.
    };
  }, [hrefs]);

  if (hrefs.length === 0) return null;

  // Render only the `<noscript>` fallback in the React tree.
  // The active links are injected via `useEffect` above.
  return (
    <noscript>
      {hrefs.map(href => (
        <link key={href} rel="stylesheet" href={href} />
      ))}
    </noscript>
  );
}

// Minimal CSS-attribute selector escape — matches what
// `CSS.escape` does for the characters that appear in Google
// Fonts URLs (`/`, `:`, `=`, `&`, `?`).
function cssAttrEscape(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}
